/**
 * Sprache und Theme — die beiden globalen Schalter, die ohne Desktop-Shell noch
 * etwas bedeuten (#504).
 *
 * Rendert nur die Felder; Überschrift und Rahmen kommen von der aufrufenden
 * Seite (`StandaloneSettingsPage`). Im Desktop erledigt das die Settings-App,
 * dort wird diese Komponente nicht verwendet.
 *
 * Das Theme ist bewusst eine Auswahl aus den verfügbaren Designs und kein
 * Hell/Dunkel-Schalter: Das Aussehen entsteht aus den CSS-Variablen, die der
 * `SettingsContext` aus dem gewählten Theme schreibt. Welche Designs zur Wahl
 * stehen, entscheidet das Backend nach Rolle und Mandant — siehe
 * `useThemeSelection`.
 *
 * Die Sprache läuft über denselben Weg wie in der Settings-App, damit die
 * Einstellung an derselben Stelle landet und mit dem Desktop übereinstimmt.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Loader2 } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { useSettingsContext } from '@/contexts/SettingsContext'
import { useThemeSelection } from '@/hooks/useThemeSelection'
import { logError } from '@/lib/logger'
import i18n from '@/i18n/config'

const LANGUAGES = ['de', 'en'] as const

export function AppearanceSettings() {
  const { t } = useTranslation()
  const { settings, saveSettings } = useSettingsContext()
  const { options, isLoading, isApplying, selectedId, selectTheme } = useThemeSelection()
  const [isSavingLanguage, setIsSavingLanguage] = useState(false)

  const applyLanguage = async (language: string) => {
    if (language === settings.language) return
    setIsSavingLanguage(true)
    try {
      await saveSettings({ ...settings, language })
      await i18n.changeLanguage(language)
    } catch (error) {
      logError(error as Error, { context: 'AppearanceSettings', action: 'language' })
    } finally {
      setIsSavingLanguage(false)
    }
  }

  const optionClass = (active: boolean) =>
    [
      'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
      active
        ? 'border-primary bg-[var(--sidebar-hover)] text-[var(--text-color)]'
        : 'border-[var(--window-border-color)] text-[var(--text-secondary)] hover:bg-[var(--sidebar-hover)]',
    ].join(' ')

  return (
    <div className="flex flex-col gap-4">
      <fieldset disabled={isSavingLanguage} className="flex flex-col gap-1.5">
        <Label>{t('settings.language.label')}</Label>
        <div className="flex gap-2">
          {LANGUAGES.map((language) => (
            <button
              key={language}
              type="button"
              onClick={() => applyLanguage(language)}
              aria-pressed={settings.language === language}
              className={`flex-1 justify-center ${optionClass(settings.language === language)}`}
            >
              {t(`languages.${language}`)}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset disabled={isApplying} className="flex flex-col gap-1.5">
        <Label>{t('settings.theme.label')}</Label>
        {isLoading ? (
          <div className="flex items-center gap-2 py-2 text-xs text-[var(--text-secondary)]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          </div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {options.map(({ value, label, icon: Icon }) => {
              const active = selectedId === value
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => selectTheme(value)}
                  aria-pressed={active}
                  className={optionClass(active)}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate text-left">{label}</span>
                  {active && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
                </button>
              )
            })}
          </div>
        )}
      </fieldset>
    </div>
  )
}
